import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import trackGTMEvents from 'shared-utils/src/trackGTMEvents/new';

import useDialogAccessibilityFeatures from 'shared-utils/src/useDialogAccessibilityFeatures';

import Arrow from 'shared-vectors/icons/Arrow';
import HpArrow from 'shared-vectors/icons/HpArrow';
import { Icon } from 'shared-vectors';

import { queryBuilder } from '@modals/factories';

import { useCtxGlobal } from '@client/app/Container';

import SearchBlock from './SearchBlock';

const propTypes = {
  value: PropTypes.string,
  matchValue: PropTypes.string,
  values: PropTypes.instanceOf(Array),
  scope: PropTypes.string,
  label: PropTypes.string,
  isOpen: PropTypes.bool,
  toggleSelect: PropTypes.func,
  storeSelectValue: PropTypes.func,
  visible: PropTypes.bool,
  availableResults: PropTypes.string,
  activeFilters: PropTypes.instanceOf(Object),
  device: PropTypes.string,
};

const defaultProps = {
  value: '',
  matchValue: '',
  values: [],
  scope: '',
  label: '',
  isOpen: false,
  toggleSelect: () => { },
  storeSelectValue: () => { },
  visible: true,
  availableResults: '',
  activeFilters: {},
  device: '',
};

const createLabel = (value, values) => {
  let categoryConf = values.filter(item => item.slug === value)[0];
  if (typeof categoryConf === 'undefined') {
    const filterOptionWithChildren = values.filter(item => 'children' in item)[0];
    categoryConf = filterOptionWithChildren ? filterOptionWithChildren.children.filter(item => item.slug === fixedRentValue)[0] : {};
  }
  return categoryConf.label === 'Immobili e attività commerciali' ? 'Imm. e att. comm.' : categoryConf.label;
};

const expandWithChildren = (evt) => {
  if (evt.target.parentNode.classList.contains('expanded')) {
    evt.target.parentNode.classList.remove('expanded');
    evt.target.parentNode.parentNode.parentNode.classList.remove('submenu');
  } else {
    evt.target.parentNode.classList.add('expanded');
    evt.target.parentNode.parentNode.parentNode.classList.add('submenu');
  }
};

const createOption = (scope, option, currentFilterValue, matchValue, storeSelectValue, isOpen) => {  
  let cssClassName = 'children' in option ? 'withChildren' : null;
  const isSelected = option.slug === currentFilterValue;

  if (isSelected) {
    cssClassName = cssClassName !== null ? `${cssClassName} selected` : 'selected';
  }
  if (scope === 'tr') {
    if (
      (
        matchValue === 'stanza'        
      ) &&
      option.slug === 'vendita'
    ) {
      cssClassName = cssClassName !== null ? `${cssClassName} disabled` : 'disabled';
    }
    if (
      matchValue !== 'casa'
      && option.slug === 'affitto-breve'
    ) {
      cssClassName = cssClassName !== null ? `${cssClassName} disabled` : 'disabled';
    }
  }
  if ('children' in option) {
    return null;
  }
  return (
    <li
      key={`${scope}_option_${Math.random()}`}
      className={cssClassName}
      role="option"
    >
      <button
        type="button"
        className="hpFormSelectBtn"
        aria-label={scope === 'category' ? `Cerca immobili nella categoria ${option.label}` : `Cerca immobili in ${option.label}`}
        onClick={(e) => {
          if ('children' in option) {
            expandWithChildren(e);
          } else {
            storeSelectValue({ [scope]: option.slug });
          }
        }}
        tabIndex={isOpen ? 0 : -1}
        aria-hidden={isOpen ? 'false' : 'true'}
      >
        {option.label}
      </button>
    </li>
  );
};

const agencyTracking = () => trackGTMEvents({
  category: 'Interaction',
  action: 'GoToAgencySearchHP',
});

const createAgencyButton = (isOpen) => (
  <li>
    <a
      aria-label="Cerca agenzie immobiliari"
      className="agencyBtn"
      href="/agenzie/"
      onClick={agencyTracking}
      tabIndex={isOpen ? 0 : -1}
      aria-hidden={isOpen ? 'false' : 'true'}
      role="button"
    >
      Agenzia
      <Icon name={Arrow} color="secondary" width="13px" height="13px" />
    </a>
  </li>
);

const Select = ({
  value,
  matchValue,
  values,
  scope,
  label,
  isOpen,
  toggleSelect,
  storeSelectValue,
  visible,
  availableResults,
  // activeFilters,
  device,
}) => {
  const el = useRef(null);
  const options = useRef(null);
  const [coords, setCoords] = useState(null);
  const [over, setOver] = useState(0);
  const [topLeft, setTopLeft] = useState(null);
    
  useDialogAccessibilityFeatures({
    element: options.current,
    isOpen,
    closefn: () => toggleSelect(scope),
    // customFocusableElements: null,
    delay: 405,
  });

  useEffect(() => {
    if (!isOpen) {
      over && setTimeout(() => {
        setOver(0);
      }, 401);
    } else {
      setOver(1);
    }
  }, [isOpen]);

  const {
    isMobile,
    isSmallMobile,
    openModal,
    pageType,
    filtersProps,
  } = useCtxGlobal();

  const openQbPtypes = () => {
    toggleSelect(scope);
    openModal && openModal(queryBuilder({
      isMobile,
      isSmallMobile,
      availableResults,
      pageType,
      filtersProps,
      panelChooseBy: {
        isMobile,
        actionClicked: 'propertyTypeGroupSelect',
        pageFrom: pageType,
      },
    }));
  };

  const action = () => {
    const { top, height } = el.current.getBoundingClientRect();
    const { height: selectH } = options.current.getBoundingClientRect();

    if (device !== 'smartphone') {
      let x = (height / 2) - (Math.ceil(selectH / 1.333) / 2);

      if (top < Math.abs(x)) {
        x = (top - 24) * -1;
      }

      setCoords(`${x}px`);
    } else if (window.scrollY) {
      if ((top + height + selectH) < (document.documentElement.clientHeight + window.scrollY)) {
        setCoords(0);
        setTopLeft({ position: 'absolute', top: '100%', left: 0 });
      }
    } else {
      setCoords(null);
      setTopLeft(null);
    }
    toggleSelect(scope);
  };

  let cls = `sentenceElement ${scope}`;
  if (isOpen) {
    cls += ' active';
  }
  if (over) {
    cls += ' over';
  }

  let style = null;
  if (isOpen) {
    style = { transform: `scaleY(1) translateY(${coords})` };
    if (topLeft) {
      style = { ...style, ...topLeft };
    }
  }
  if (!isOpen && over && topLeft) {
    style = { position: 'absolute', top: '100%', left: 0 };
  } 

  const btnValue = createLabel(value, values);

  const onKeyPress = (e) => {
    if (e.key === 'Enter') {
      action();
    }
  };

  return (
    <div
      id={`${scope}Select`}
      className={cls}
      style={!visible ? { display: 'none' } : {}}
      ref={el}
    >
      <SearchBlock
        label={label}
        button={btnValue}
        onClickAction={action}
        onKeyPressAction={onKeyPress}
        icon={<Icon aria-hidden="true" name={HpArrow} width="12px" height="7px" className={`is-anim mb--xs${isOpen ? ' rotate--180' : ''}`} fill />}
        ariaLabel={scope === 'category' ? `Cerco ${btnValue}` : `In ${btnValue}`}
        ariaDescription={scope === 'category' ? 'Clicca per cambiare tipologia immobile' : 'Clicca per cambiare tipologia di contratto'}
        ariaHaspopup="listbox"
        ariaControls={`${scope}SelectOptions`}
        ariaExpanded={isOpen}
      />
      <div
        ref={options}
        {...(!isOpen ? { inert: "" } : {})}
        className={`hpFormSelectOptions${isOpen ? ' active' : ''}`}
        style={style}        
      >
         <ul
          id={`${scope}SelectOptions`}
          role="listbox"
          aria-label={scope === 'category' ? 'Scegli tipologia immobile' : 'Scegli tipologia di contratto'}
          tabIndex={isOpen ? 0 : -1}
          aria-hidden={isOpen ? 'false' : 'true'}
        >
          {values.map(option => createOption(scope, option, value, matchValue, storeSelectValue, isOpen))}
          {
            scope === 'propertyTypeGroup'
              && (
                <>
                  <li className="hpFormSelectMdlCtn mt--m">
                    <button
                      type="button"
                      className="hpFormSelectMdl tp-s--l tp-w--m c-txt--secondary tp--u"
                      onClick={openQbPtypes}
                      tabIndex={isOpen ? 0 : -1}
                      aria-hidden={isOpen ? 'false' : 'true'}
                    >
                      Vedi lista completa
                    </button>
                  </li>
                  {createAgencyButton(isOpen)}
                </>
              )
          }
        </ul>
      </div>
    </div>
  );
};
Select.propTypes = propTypes;
Select.defaultProps = defaultProps;
export default Select;
